/**
 * ModeDefinition v1 검증 — ① JSON Schema(ajv 2020, strict) ② 의미 규칙(스키마로 못 거르는 것):
 *  - id 겹침(공통 env + 프리셋 env) · 조건이 가리키는 존·환경이 있는가 · 프리셋 키 겹침
 *  - 프리팹별 필수 값(size) · 존은 finish-zone 만 · 타이머 onExpire 와 when:timer 조건이 둘 다 있으면 모호
 *  - P3 릴레이(room.relay): 구간 수 상·하한 · 타임아웃 선택지 · 구간 틀 안의 자리(FIELD-DEC-003)
 * 운영 콘솔 배포 전 게이트(SR-MODE-01 · SR-CAT-02)이자 커널 로드 게이트다.
 */
import { Ajv2020 } from "ajv/dist/2020.js";
import schema from "../schema/mode-definition-v1.schema.json" with { type: "json" };
import { PREFABS } from "./prefabs";
import type { EnvPlacement, ModeDefinitionV1, Vec3 } from "./types";

export interface ModeIssue {
  readonly path: string;
  readonly message: string;
}

export type ModeValidation =
  | { readonly ok: true; readonly def: ModeDefinitionV1 }
  | { readonly ok: false; readonly issues: readonly ModeIssue[] };

const ajv = new Ajv2020({ allErrors: true, strict: true, strictRequired: false });
const schemaCheck = ajv.compile<ModeDefinitionV1>(schema);

/**
 * 모드 정의 개정(version) — 같은 스키마(v1)의 개정판. 제어 평면은 게시한 버전을 바꾸지 않으므로(불변) 내용이 바뀌면 개정을 올린다.
 * 2 = 기구 무대 '센서 손' · 정답 기준 목표(2026-10-06 — machine-stage.v2).
 * 3 = 기구 무대 판 기울기(stage.tilt — 시소 판이 허브 기울기를 따라간다 · 2026-10-06 — machine-stage.v3).
 * 4 = 모터로 시소 흔들기(SP-D08 stage.tilt.legs · 판 위 허브 기울기 · 별 = 흔들기 횟수 · 2026-10-06 — machine-stage.v4).
 * 5 = 시소 숨은 보너스 '한 번 흔든 뒤 수평에서 삐'(beeps 에 joint · range · amp — 2026-10-06 — machine-stage.v5).
 * 6 = 정적 기계 16차시 무대(로봇 팔 · 색상 분류기 · 프레스 · 플로터 · 펀치 · 배구 …) — 프리셋만 더함(2026-10-07 — machine-stage.v6).
 * 7 = 프레스(SP-C03) · 주사위(SP-K19) 별 기준 — 정답 왕복 · 모형에 맞는 모터(관절 스펙 기어비 · cycle)(2026-10-07 — machine-stage.v7).
 * 8 = 탁구공 발사기(SP-K29) 별 기준을 휠(B)로 — 정답 포트가 모형과 맞게(2026-10-07 — machine-stage.v8).
 * 9~ = 운영 콘솔 '무대 목표'가 만든 판(서버 DB 에만 — 2026-10-08). 저장소 판을 올릴 때는 운영 DB 의 가장 큰 버전 다음 번호 · 그 내용을 이어받는다.
 */
export const SUPPORTED_MODE_VERSIONS: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8];

/** 조건이 이 프리셋에서 쓰이나(presets · skipPresets — 2026-10-07) */
export function conditionApplies(
  c: { readonly presets?: readonly string[]; readonly skipPresets?: readonly string[] },
  presetKey: string,
): boolean {
  if (c.presets && !c.presets.includes(presetKey)) return false;
  if (c.skipPresets?.includes(presetKey)) return false;
  return true;
}

export function validateModeDefinition(input: unknown): ModeValidation {
  if (!schemaCheck(input))
    return {
      ok: false,
      issues: (schemaCheck.errors ?? []).map((e) => ({
        path: e.instancePath || "/",
        message: `${e.message ?? "invalid"}${e.params && Object.keys(e.params).length ? ` ${JSON.stringify(e.params)}` : ""}`,
      })),
    };
  const def = input;
  const issues: ModeIssue[] = [];
  // 2026-10-08 — 운영 콘솔(무대 목표 편집기)이 저장소 판 다음 버전을 만든다: 버전은 정의 행 번호일 뿐(동작 차이 없음)이라
  // 1 이상의 정수면 받는다. SUPPORTED_MODE_VERSIONS 는 저장소 판 기록으로 남긴다
  if (!Number.isInteger(def.version) || def.version < 1)
    issues.push({ path: "/version", message: `지원하지 않는 version ${def.version}` });
  const keys = new Set<string>();
  def.presets.forEach((p, pi) => {
    if (keys.has(p.key))
      issues.push({ path: `/presets/${pi}/key`, message: `프리셋 키 겹침 ${p.key}` });
    keys.add(p.key);
    const env: [EnvPlacement, string][] = [
      ...def.env.map((e, i): [EnvPlacement, string] => [e, `/env/${i}`]),
      ...p.env.map((e, i): [EnvPlacement, string] => [e, `/presets/${pi}/env/${i}`]),
    ];
    const ids = new Map<string, EnvPlacement>();
    for (const [e, path] of env) {
      if (ids.has(e.id)) issues.push({ path: `${path}/id`, message: `환경 id 겹침 ${e.id}` });
      ids.set(e.id, e);
      const need = PREFABS[e.prefab].needs;
      if (need.includes("size") && !e.size)
        issues.push({ path, message: `${e.prefab} 에는 size 가 필요하다` });
    }
    def.conditions.forEach((c, ci) => {
      const path = `/conditions/${ci}`;
      if (!conditionApplies(c, p.key)) return;
      if (c.zone !== undefined) {
        const z = ids.get(c.zone);
        if (!z || PREFABS[z.prefab].kind !== "zone")
          issues.push({ path, message: `프리셋 ${p.key}: 존 '${c.zone}' 없음` });
      }
      if (c.object !== undefined) {
        const o = ids.get(c.object);
        if (!o || PREFABS[o.prefab].kind === "zone")
          issues.push({ path, message: `프리셋 ${p.key}: 환경 '${c.object}' 없음` });
      }
    });
  });
  def.presets.forEach((p, pi) => {
    if (
      p.friendSpawns &&
      def.players.friends !== undefined &&
      p.friendSpawns.length < def.players.friends
    )
      issues.push({
        path: `/presets/${pi}/friendSpawns`,
        message: `친구 자리 ${p.friendSpawns.length} < players.friends ${def.players.friends}`,
      });
  });
  const g = def.gallery;
  if (g) {
    if (g.pedestals.length < g.maxExhibits)
      issues.push({
        path: "/gallery/pedestals",
        message: `받침대 ${g.pedestals.length} < maxExhibits ${g.maxExhibits}`,
      });
    if (g.activation.maxConcurrent > g.maxExhibits)
      issues.push({
        path: "/gallery/activation/maxConcurrent",
        message: `동시 작동 한도 ${g.activation.maxConcurrent} > maxExhibits ${g.maxExhibits}`,
      });
    // 받침대끼리 겹치면 안 된다(90° 단위 — 겹침 = 두 사각형이 겹침)
    const [sx, , sz] = g.pedestal.size;
    g.pedestals.forEach((a, i) =>
      g.pedestals.slice(0, i).forEach((b, j) => {
        if (
          Math.abs(a.at[0] - b.at[0]) < Math.max(sx, sz) &&
          Math.abs(a.at[2] - b.at[2]) < Math.max(sx, sz)
        )
          issues.push({
            path: `/gallery/pedestals/${i}`,
            message: `받침대 ${i} 가 ${j} 와 겹친다`,
          });
      }),
    );
  }
  const r = def.room?.relay;
  if (r) {
    // P3 릴레이(FIELD-DEC-003 §3) — 스키마로 못 거르는 것: 상·하한 · 타임아웃 선택지 · 구간 틀 안의 자리
    if ((r.minStages ?? 2) > r.maxStages)
      issues.push({
        path: "/room/relay/minStages",
        message: `minStages ${r.minStages} > maxStages ${r.maxStages}`,
      });
    if (r.timeoutChoicesS && !r.timeoutChoicesS.includes(r.stageTimeoutS))
      issues.push({
        path: "/room/relay/timeoutChoicesS",
        message: `기본 타임아웃 ${r.stageTimeoutS} 이 선택지에 없다`,
      });
    const hx = r.section.size[0] / 2;
    const hz = r.section.size[2] / 2;
    const inside = (at: Vec3, half: readonly [number, number] = [0, 0]) =>
      Math.abs(at[0]) + half[0] <= hx + 1e-9 && Math.abs(at[2]) + half[1] <= hz + 1e-9;
    for (const k of ["machine", "entry", "object"] as const)
      if (!inside(r.section[k].at))
        issues.push({ path: `/room/relay/section/${k}`, message: `${k} 가 구간 틀 밖` });
    const ex = r.section.exit;
    if (!inside(ex.at, [ex.size[0] / 2, ex.size[2] / 2]))
      issues.push({ path: "/room/relay/section/exit", message: "나감 상자가 구간 틀 밖" });
    if (ex.at[0] <= r.section.entry.at[0])
      issues.push({
        path: "/room/relay/section/exit",
        message: "나감 상자는 들어옴 자리보다 흐름 방향(+x) 앞이어야 한다",
      });
    if (def.players.max < (r.minStages ?? 2))
      issues.push({ path: "/players/max", message: "players.max < 릴레이 minStages" });
    // P3-S4 Q-3 — 릴레이는 반 룸만(교류 룸 60 명 제외)
    if (def.players.max > 30)
      issues.push({ path: "/players/max", message: "릴레이는 반 룸만 — players.max ≤ 30" });
  }
  // 기구 무대 — 별 조건은 모든 프리셋에 stage 가 있어야 뜻이 있다
  def.conditions.forEach((c, i) => {
    if (c.stars === undefined) return;
    if (c.when !== "stars" && c.when !== "timer")
      issues.push({
        path: `/conditions/${i}/stars`,
        message: "stars 는 when:stars · when:timer 에만",
      });
    def.presets.forEach((p) => {
      if (!p.stage)
        issues.push({
          path: `/conditions/${i}`,
          message: `별 조건인데 프리셋 ${p.key} 에 stage 가 없다`,
        });
    });
  });
  def.presets.forEach((p, pi) => {
    for (const [gi, g] of [
      ...(p.stage?.goals ?? []),
      ...(p.stage?.bonus ? [p.stage.bonus] : []),
    ].entries())
      for (const k of ["rate", "range"] as const) {
        const r = g[k];
        if (r && !(r[0] < r[1]))
          issues.push({
            path: `/presets/${pi}/stage/goals/${gi}/${k}`,
            message: `${k} 는 [작은 값, 큰 값]`,
          });
      }
  });
  if (def.timer?.onExpire && def.conditions.some((c) => c.when === "timer"))
    issues.push({
      path: "/timer/onExpire",
      message: "timer.onExpire 와 when:timer 조건을 함께 쓰면 모호하다 — 하나만",
    });
  return issues.length ? { ok: false, issues } : { ok: true, def };
}

export { schema as modeDefinitionV1Schema };
